import React, { FC, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import CustomInput from '../../Components/Inputs/CustomInput'; // Your custom input component
import { createFolder, updateFolder } from '../../api/services'; // API calls

// Folder type enum
export enum FolderType {
  IMAGES = 'images',
  VIDEOS_REELS = 'videos_reels',
  DOCUMENTS = 'documents',
  // VIDEOS_DRONE_SHOOTS = 'videos_drone_shoots',
  ALL_TYPES = 'all_types',
}

// Props for the Folder Management Drawer
interface CreateFolderDrawerProps {
  open: boolean;
  onClose: () => void;
  initialValues?: FolderFormValues; // For editing an existing folder
  folderId?: string; // ID of the folder being edited (if any)
  parent?: string; // Parent folder ID
}

// Form values type
interface FolderFormValues {
  name: string;
  parent ?: string;
}

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required('Folder name is required'),

});

const CreateFolderDrawer: FC<CreateFolderDrawerProps> = ({ open, onClose, initialValues, folderId, parent }) => {
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FolderFormValues>({
    resolver: yupResolver(schema),
    defaultValues: initialValues || {
      name: '',
      parent: parent || '',
    },
  });

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    } else {
      reset({
        name: '',
        parent: parent || '',

      });
    }
  }, [initialValues, reset]);

  // Create folder mutation
  const createMutation = useMutation((data: FolderFormValues) => createFolder(data), {
    onSuccess: () => {
      toast.success('Folder created successfully!');
      queryClient.invalidateQueries('alltabs'); 
      queryClient.invalidateQueries('tabs');
      queryClient.invalidateQueries('folderData');
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create folder');
    },
  });

  const updateMutation = useMutation((data: FolderFormValues) => {
    if (folderId) {
      return updateFolder(folderId, data);
    }
    return Promise.reject(new Error('Folder ID is undefined'));
  }, {
    onSuccess: () => {
      toast.success('Folder updated successfully!');
      queryClient.invalidateQueries('alltabs'); 
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update folder');
    },
  });

  // Handle form submission
  const onSubmit = (data: FolderFormValues) => {
    createMutation.mutate(data);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 400,
          background: "white",
          borderRadius: 0,
        },
      }}
    >
      {/* Header */}      
      <Box
        sx={{
          width: '100%',
          height: 60,
          // marginTop: "64px",
          backgroundColor: '#0f63a5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          color: 'white',
          zIndex: (theme) => theme.zIndex.drawer + 10,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {folderId ? 'Edit Folder' : 'Create Folder'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ padding: '20px' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Folder Name */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel shrink sx={{ ml: -1.4 }} htmlFor="name-input">
              Folder Name
            </InputLabel>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CustomInput
                  id="name-input"
                  placeholder="Enter folder name"
                  {...field}
                  error={!!errors.name}
                />
              )}
            />
            <FormHelperText error>{errors.name?.message}</FormHelperText>
          </FormControl>

          {/* parent */}
          <Controller
            name="parent"
            control={control}
            render={({ field }) => (
              <CustomInput
                id="parent-input"
                disabled
                {...field}
                error={!!errors.parent}
              />
            )}
          />


          {/* Folder Description */}
         
          {/* Folder Type */}
          {/* <InputLabel shrink sx={{}} htmlFor="type-select">
              Folder Type
            </InputLabel>
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  id="type-select"
                  displayEmpty
                  {...field}
                  error={!!errors.type}
                  
                  sx={{
                    width: '100%',
                    height: "40px",
                    border: "1px solid #1212121A",
                    // borderRadius: "20px",
                    opacity: 1,
                    boxShadow: "0px 6px 14px #36408D08",
                    fontSize: "12px",
                    color: "#1D1D1D",
                    textAlign: 'left',
                  }}
                >
                  {Object.values(FolderType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText error>{errors.type?.message}</FormHelperText>
          </FormControl> */}

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" color="error" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              {'Create Folder'}
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default CreateFolderDrawer;
